<template>
  <span class="snat-ticket-wrap">
    <button
      v-if="!label"
      type="button"
      class="barra-superior-icon-btn"
      aria-label="Agregar ticket SNAT"
      title="Agregar ticket SNAT (soft-dev)"
      :disabled="loading || saving"
      @click="openDialog"
    >
      <i class="pi pi-ticket" aria-hidden="true" />
    </button>
    <Button
      v-else
      type="button"
      :label="label"
      icon="pi pi-ticket"
      :class="buttonClass || 'p-button-sm p-button-help'"
      :disabled="loading || saving"
      @click="openDialog"
    />

    <Dialog
      v-model:visible="open"
      header="Agregar ticket SNAT (soft-dev)"
      :modal="true"
      :style="{ width: 'min(700px, 96vw)' }"
    >
      <Message v-if="ctxError" severity="error" :closable="false" class="mb-2">{{ ctxError }}</Message>
      <Message v-if="submitError" severity="warn" :closable="false" class="mb-2">{{ submitError }}</Message>

      <div class="grid">
        <div class="col-12 md:col-6">
          <label class="snat-label">Cliente SNAT</label>
          <Dropdown
            v-model="form.client_id"
            :options="clients"
            option-label="name"
            option-value="id"
            placeholder="Seleccione cliente"
            class="w-full"
            :disabled="loading || saving"
            @change="onClientChange"
          />
        </div>
        <div class="col-12 md:col-6">
          <label class="snat-label">Sistema (proyecto)</label>
          <Dropdown
            v-model="form.project_id"
            :options="projectsByClient"
            option-label="name"
            option-value="id"
            placeholder="Opcional"
            class="w-full"
            show-clear
            :disabled="loading || saving || !form.client_id"
            @change="onProjectChange"
          />
        </div>
        <div class="col-12 md:col-6">
          <label class="snat-label">Ambiente</label>
          <Dropdown
            v-model="form.project_environment_id"
            :options="envsByProject"
            option-label="name"
            option-value="id"
            placeholder="Opcional"
            class="w-full"
            show-clear
            :disabled="loading || saving || !form.project_id"
          />
        </div>
        <div class="col-12 md:col-6">
          <label class="snat-label">Componente</label>
          <Dropdown
            v-model="form.infrastructure_asset_id"
            :options="componentsByClientProject"
            option-label="name"
            option-value="id"
            placeholder="Opcional"
            class="w-full"
            show-clear
            :disabled="loading || saving || !form.client_id"
          />
        </div>
        <div class="col-12 md:col-6">
          <label class="snat-label">Tipo</label>
          <Dropdown
            v-model="form.kind"
            :options="kindChoices"
            option-label="label"
            option-value="id"
            class="w-full"
            :disabled="loading || saving"
          />
        </div>
        <div class="col-12 md:col-6">
          <label class="snat-label">Prioridad</label>
          <Dropdown
            v-model="form.priority"
            :options="priorityChoices"
            option-label="label"
            option-value="id"
            class="w-full"
            :disabled="loading || saving"
          />
        </div>
        <div class="col-12">
          <label class="snat-label">Pantalla / componente UI</label>
          <InputText
            v-model="form.screen_component"
            class="w-full"
            placeholder="Ej: CatalogoProductos > filtro categoría > botón aplicar"
            :disabled="saving"
          />
          <small class="text-500">Ruta detectada: <code>{{ currentRoutePath }}</code></small>
        </div>
        <div class="col-12">
          <label class="snat-label">Asunto</label>
          <InputText v-model="form.subject" class="w-full" :disabled="saving" maxlength="255" />
        </div>
        <div class="col-12">
          <label class="snat-label">Descripción</label>
          <Textarea v-model="form.description" rows="7" class="w-full" :disabled="saving" />
        </div>
        <div v-if="offerClearRequirementsAfterSuccess" class="col-12 snat-clear-req">
          <Checkbox
            v-model="clearRequirementsAfterSuccess"
            input-id="snat-clear-req-notebook"
            :binary="true"
            :disabled="saving"
          />
          <label for="snat-clear-req-notebook" class="snat-clear-req-label">
            Vaciar cuaderno de requerimientos UI al crear el ticket
          </label>
        </div>
      </div>

      <template #footer>
        <Button label="Cancelar" class="p-button-text" :disabled="saving" @click="open = false" />
        <Button label="Crear ticket SNAT" icon="pi pi-check" :loading="saving" :disabled="loading" @click="submit" />
      </template>
    </Dialog>
  </span>
</template>

<script>
import axios from 'axios';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import { applySnatTicketEnvDefaults, getSnatEmbedTokenPair } from '@/utils/snatTicketEnvDefaults';
import { clearRequirementsNotebook } from '@/utils/requirementsNotebook';
import {
  applySnatInfraEnvDefaults,
  buildSnatTicketContextPayload,
} from '@/utils/snatTicketPayloadExtras';

function baseTiendaUrl() {
  return (process.env.VUE_APP_API_URL_TIENDA || '').replace(/\/?$/, '/');
}

function authHeaders() {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('access_token')) || '';
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

export default {
  name: 'SnatTicketButton',
  components: { Button, Dialog, Dropdown, InputText, Message, Textarea, Checkbox },
  props: {
    sourceModule: { type: String, default: 'kosexa_front' },
    prefillSubject: { type: String, default: '' },
    prefillDescription: { type: String, default: '' },
    kindDefault: { type: String, default: '' },
    label: { type: String, default: '' },
    buttonClass: { type: String, default: '' },
    /** Muestra opción para vaciar el cuaderno de requerimientos UI tras un alta exitosa (solo aplica si el usuario la marca). */
    offerClearRequirementsAfterSuccess: { type: Boolean, default: false },
    /**
     * Tokens de despliegue (mismos que en soft_dev y VUE_APP_SNAT_DEFAULT_*_TOKEN). Si se pasan, sustituyen al .env
     * para fijar cliente/proyecto en una vista o componente concreto.
     */
    embedClientToken: { type: String, default: '' },
    embedProjectToken: { type: String, default: '' },
  },
  data() {
    return {
      clearRequirementsAfterSuccess: true,
      open: false,
      loading: false,
      saving: false,
      ctxError: '',
      submitError: '',
      clients: [],
      projects: [],
      envs: [],
      componentsList: [],
      kindChoices: [],
      priorityChoices: [],
      form: {
        client_id: null,
        project_id: null,
        project_environment_id: null,
        infrastructure_asset_id: null,
        kind: 'support',
        priority: 'normal',
        subject: '',
        description: '',
        screen_component: '',
      },
    };
  },
  computed: {
    currentRoutePath() {
      return this.$route.fullPath || this.$route.path || '';
    },
    projectsByClient() {
      return this.projects.filter((p) => Number(p.client_id) === Number(this.form.client_id));
    },
    envsByProject() {
      return this.envs.filter((e) => Number(e.project_id) === Number(this.form.project_id));
    },
    componentsByClientProject() {
      return this.componentsList.filter((c) => {
        if (Number(c.client_id) !== Number(this.form.client_id)) return false;
        if (!this.form.project_id) return true;
        return c.project_id == null || Number(c.project_id) === Number(this.form.project_id);
      });
    },
  },
  methods: {
    onClientChange() {
      this.form.project_id = null;
      this.form.project_environment_id = null;
      this.form.infrastructure_asset_id = null;
    },
    onProjectChange() {
      this.form.project_environment_id = null;
    },
    async openDialog() {
      this.open = true;
      this.clearRequirementsAfterSuccess = true;
      if (!this.clients.length && !this.loading) await this.loadContext();
      else {
        await this.$nextTick();
        this.applyPrefillFromProps();
      }
    },
    applyPrefillFromProps() {
      if (this.prefillSubject) this.form.subject = String(this.prefillSubject).slice(0, 255);
      if (this.prefillDescription) this.form.description = String(this.prefillDescription).slice(0, 12000);
      if (this.kindDefault && Array.isArray(this.kindChoices)) {
        const ok = this.kindChoices.some((k) => k.id === this.kindDefault);
        if (ok) this.form.kind = this.kindDefault;
      }
    },
    contextQueryString() {
      const { client, project } = getSnatEmbedTokenPair(this.embedClientToken, this.embedProjectToken);
      const p = new URLSearchParams();
      if (client) p.set('client_token', client);
      if (project) p.set('project_token', project);
      const s = p.toString();
      return s ? `?${s}` : '';
    },
    async loadContext() {
      this.loading = true;
      this.ctxError = '';
      try {
        const { data } = await axios.get(
          `${baseTiendaUrl()}operaciones/snat-ticket/context/${this.contextQueryString()}`,
          { headers: authHeaders() },
        );
        if (data.status !== 'OK') throw new Error(data.message || 'No se pudo cargar contexto SNAT');
        const d = data.data || {};
        this.clients = d.clients || [];
        this.projects = d.projects || [];
        this.envs = d.environments || [];
        this.componentsList = d.components || [];
        this.kindChoices = d.kind_choices || [];
        this.priorityChoices = d.priority_choices || [];
        if (d.default_client_id) this.form.client_id = Number(d.default_client_id);
        applySnatTicketEnvDefaults(this.form, this.clients, this.projects, d);
        applySnatInfraEnvDefaults(this.form, this.envs, this.componentsList);
        this.applyPrefillFromProps();
      } catch (e) {
        this.ctxError = e.response?.data?.message || e.message || 'No se pudo cargar el contexto SNAT.';
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      this.submitError = '';
      if (!this.form.client_id) {
        this.submitError = 'Seleccione cliente SNAT.';
        return;
      }
      if (!this.form.subject.trim()) {
        this.submitError = 'El asunto es obligatorio.';
        return;
      }
      this.saving = true;
      try {
        const ctxExtra = buildSnatTicketContextPayload(this.form, this.envs, this.componentsList);
        const toks = getSnatEmbedTokenPair(this.embedClientToken, this.embedProjectToken);
        const payload = {
          ...this.form,
          source_module: this.sourceModule,
          route_name: this.$route.name != null ? String(this.$route.name) : '',
          screen_path: this.currentRoutePath,
          subject: this.form.subject.trim(),
          ...ctxExtra,
        };
        if (toks.client) payload.embed_client_token = toks.client;
        if (toks.project) payload.embed_project_token = toks.project;
        const { data } = await axios.post(`${baseTiendaUrl()}operaciones/snat-ticket/create/`, payload, {
          headers: authHeaders(),
        });
        if (data.status !== 'OK') throw new Error(data.message || 'No se pudo crear ticket');
        if (this.offerClearRequirementsAfterSuccess && this.clearRequirementsAfterSuccess) {
          clearRequirementsNotebook();
        }
        this.open = false;
      } catch (e) {
        this.submitError = e.response?.data?.message || e.message || 'No se pudo crear el ticket SNAT.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.snat-label {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.snat-clear-req {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding-top: 0.25rem;
}

.snat-clear-req-label {
  font-size: 0.85rem;
  line-height: 1.4;
  color: #334155;
  cursor: pointer;
  user-select: none;
}
</style>

